import gql from 'graphql-tag';
import { USER_FIELDS } from '../users/queries';
import { VotingAreaFields } from '../voting_areas/queries';

const ORGANIZATION_FIELDS = gql`
  fragment OrganizationFields on ri_apps_prd_organizations {
    organization_id
    name
    first_name
    last_name
    email
    phone
    #is_active
    start_date
    end_date
    flg_prod
    state_timezone
    state_has_multiple_timezones
    sms_provider
    organizations_status {
      status_id
      name
      code
    }
    organization_org_billing_detail {
      org_billing_detail_id
    }
    organizations_organization_voting_areas {
      organization_voting_areas_voting_area {
        ...VotingAreaFields
      }
    }
    sms_provider
    direct_snd_api_key
    organization_type_id
    #voting_area_states
  }
  ${VotingAreaFields}
`;

const ORG_BILLING_DETAIL_FRAGMENT = gql`
  fragment OrgBillingDetail on ri_apps_prd_org_billing_details {
    org_billing_detail_id
    prod_stripe_customer_id
    test_stripe_customer_id
    name
    phone
    email
    address_line_1
    address_line_2
    city
    state
    country
    postal_code
    stripe_contract_price
    payment_term
    stripe_test_mode
    is_autopay_enabled
    requires_task_payment
    is_bank_transfer_only
    transaction_fees
  }
`;

export const GET_ORGANIZATIONS_FOR_PHONE_MANAGEMENT = gql`
  query GetOrganizationsForPhoneManagement(
    $where: ri_apps_prd_organizations_bool_exp
    $order_by: [ri_apps_prd_organizations_order_by!]
  ) {
    rows: ri_apps_prd_organizations(where: $where, order_by: $order_by) {
      ...OrganizationFields
      organizations_org_phone_numbers {
        org_phone_number_id
        twilio_id
        phone_number
        purpose_code
        messaging_service_id
      }
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const GET_ORGANIZATIONS = gql`
  query GetOrganizations(
    $where: ri_apps_prd_organizations_bool_exp
    $order_by: [ri_apps_prd_organizations_order_by!]
  ) {
    rows: ri_apps_prd_organizations(where: $where, order_by: $order_by) {
      ...OrganizationFields
      organizations_user_organizations_aggregate(
        where: {
          flg_inactive: { _eq: "N" }
          user_organizations_user: { flg_inactive: { _eq: "N" } }
        }
        distinct_on: [user_id]
      ) {
        aggregate {
          count
        }
      }
      organizations_campaigns {
        campaign_id
        campaigns_task_campaigns(
          where: { task_campaigns_task: { bulk_task_id: { _is_null: true } } }
        ) {
          task_id
        }
      }
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const GET_ORGANIZATIONS_FOR_MANAGER = gql`
  query GetOrganizationsForManager($user_id: Int!) {
    rows: ri_apps_prd_organizations(
      where: {
        organizations_user_organizations: { user_id: { _eq: $user_id }, flg_inactive: { _eq: "N" } }
      }
    ) {
      ...OrganizationFields
      organizations_user_organizations {
        user_id
        flg_inactive
        user_organizations_role {
          code
          name
        }
      }
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const GET_ORGANIZATION_BY_ID = gql`
  query GetOrganizationById($org_id: Int!) {
    row: ri_apps_prd_organizations_by_pk(organization_id: $org_id) {
      ...OrganizationFields
      sms_price
      sms_subsequent_price
      mms_image_price
      mms_video_price
      call_price
      voice_recording_price
      organizations_organization_voting_areas {
        organization_voting_areas_voting_area {
          ...VotingAreaFields
        }
      }
    }
  }
  ${ORGANIZATION_FIELDS}
  ${VotingAreaFields}
`;

export const GET_ORGANIZATION_WITH_BILLING_DETAILS_BY_ID = gql`
  query GetOrganizationById($org_id: Int!) {
    row: ri_apps_prd_organizations_by_pk(organization_id: $org_id) {
      ...OrganizationFields
      sms_price
      sms_subsequent_price
      mms_image_price
      mms_video_price
      call_price
      voice_recording_price
      organization_org_billing_detail {
        ...OrgBillingDetail
      }
      organizations_organization_voting_areas {
        organization_voting_areas_voting_area {
          ...VotingAreaFields
        }
      }
    }
  }
  ${ORGANIZATION_FIELDS}
  ${VotingAreaFields}
  ${ORG_BILLING_DETAIL_FRAGMENT}
`;

export const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($data: ri_apps_prd_organizations_insert_input!) {
    row: insert_ri_apps_prd_organizations_one(object: $data) {
      organization_id
    }
  }
`;

export const UPDATE_ORGANIZATION = gql`
  mutation UpdateOrganization($org_id: Int!, $data: ri_apps_prd_organizations_set_input!) {
    row: update_ri_apps_prd_organizations_by_pk(
      pk_columns: { organization_id: $org_id }
      _set: $data
    ) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const DELETE_USER_ORGANIZATIONS = gql`
  mutation DeleteUserOrganizations($org_id: Int!) {
    row: delete_ri_apps_prd_user_organizations(where: { organization_id: { _eq: $org_id } }) {
      affected_rows
    }
  }
`;

export const DELETE_ORGANIZATION = gql`
  mutation DeleteOrganization($org_id: Int!) {
    row: delete_ri_apps_prd_organizations_by_pk(organization_id: $org_id) {
      organization_id
    }
  }
`;

export const ADD_ORG_USER = gql`
  mutation AddOrgUser($data: ri_apps_prd_user_organizations_insert_input!) {
    row: insert_ri_apps_prd_user_organizations_one(object: $data) {
      user_organization_id
      organization_id
      user_id
      user_organizations_role {
        role_id
        name
        code
      }
    }
  }
`;

export const GET_ORG_USER_MEMBERSHIP_INCLUDING_ARCHIVED = gql`
  query GetOrgUserMembershipIncludingArchived($org_id: Int!, $user_id: Int!) {
    rows: ri_apps_prd_user_organizations(
      where: {
        organization_id: { _eq: $org_id }
        user_id: { _eq: $user_id }
      }
    ) {
      user_organization_id
      flg_inactive
      role_id
    }
  }
`;

export const UPDATE_ORG_USER = gql`
  mutation UpdateOrgUser(
    $org_id: Int!
    $user_id: Int!
    $data: ri_apps_prd_user_organizations_set_input!
  ) {
    row: update_ri_apps_prd_user_organizations(
      where: { organization_id: { _eq: $org_id }, user_id: { _eq: $user_id } }
      _set: $data
    ) {
      affected_rows
    }
  }
`;

export const REMOVE_ORG_USER = gql`
  mutation RemoveOrgUser($org_id: Int!, $user_id: Int!) {
    row: delete_ri_apps_prd_user_organizations_by_pk(organization_id: $org_id, user_id: $user_id) {
      user_id
      organization_id
    }
  }
`;

const ORG_USER_FIELDS = gql`
  fragment OrgUserFields on ri_apps_prd_user_organizations {
    user_organization_id
    organization_id
    user_id
    user_organizations_role {
      role_id
      name
      code
    }
    user_organizations_organization {
      name
    }
    user_organizations_user {
      ...UserFields
    }
    user_organizations_user_campaigns {
      user_campaign_id

      user_campaigns_campaign {
        campaign_id
        name
      }
    }
  }
  ${USER_FIELDS}
`;

export const GET_ORG__USER = gql`
  query GetOrgUser($org_id: Int!, $user_id: Int!) {
    row: ri_apps_prd_user_organizations(
      where: { organization_id: { _eq: $org_id }, user_id: { _eq: $user_id } }
    ) {
      ...OrgUserFields
    }
  }
  ${ORG_USER_FIELDS}
`;

export const GET_ORG__USER_2 = gql`
  query GetOrgUser($user_id: Int!) {
    row: ri_apps_prd_user_organizations(where: { user_id: { _eq: $user_id } }) {
      ...OrgUserFields
    }
  }
  ${ORG_USER_FIELDS}
`;

export const PHONE_NUMBER_ORG_FIELDS = gql`
  fragment PhoneNumberOrgFields on ri_apps_prd_org_phone_numbers {
    org_phone_number_id
    organization_id
    phone_number
    twilio_id
    messaging_service_id
    purpose_code
    org_phone_numbers_organization {
      organization_id
      name
      sms_provider
    }
  }
`;

export const GET_ORGANIZATION_NUMBERS = gql`
  query getOrganizationNumber($orgId: Int!, $purposeCode: String_comparison_exp!) {
    ri_apps_prd_org_phone_numbers(
      where: { organization_id: { _eq: $orgId }, purpose_code: $purposeCode }
      order_by: { org_phone_numbers_organization: { name: asc } }
    ) {
      ...PhoneNumberOrgFields
    }
  }
  ${PHONE_NUMBER_ORG_FIELDS}
`;

export const GET_ALL_NUMBERS = gql`
  query getOrganizationNumber($purposeCode: String_comparison_exp) {
    ri_apps_prd_org_phone_numbers(
      where: { purpose_code: $purposeCode }
      order_by: { org_phone_numbers_organization: { name: asc } }
    ) {
      ...PhoneNumberOrgFields
    }
  }
  ${PHONE_NUMBER_ORG_FIELDS}
`;

// Like GET_ALL_NUMBERS but also includes every phone number belonging to the
// configured Twilio SMS sender orgs, regardless of purpose code.
export const GET_ALL_NUMBERS_INCLUDING_SMS_SENDER_ORGS = gql`
  query getOrganizationNumber($purposeCode: String_comparison_exp, $smsSenderOrgIds: [Int!]) {
    ri_apps_prd_org_phone_numbers(
      where: {
        _or: [{ purpose_code: $purposeCode }, { organization_id: { _in: $smsSenderOrgIds } }]
      }
      order_by: { org_phone_numbers_organization: { name: asc } }
    ) {
      ...PhoneNumberOrgFields
    }
  }
  ${PHONE_NUMBER_ORG_FIELDS}
`;

export const GET_REPLIES_BY_NUMBER_AND_DATE = gql`
  query getIncomingSMSesByReceiver(
    $phone_number: String!
    $after: timestamptz
    $before: timestamptz
    $limit: Int
    $offset: Int
    $search: ri_apps_prd_incoming_smses_bool_exp = {}
  ) {
    ri_apps_prd_incoming_smses(
      where: {
        _and: [
          { receiving_number: { _eq: $phone_number } }
          { sent_at: { _gte: $after } }
          { sent_at: { _lte: $before } }
          $search
        ]
      }
      limit: $limit
      offset: $offset
      order_by: { created_at: desc }
    ) {
      id
      sender_number
      receiving_number
      sent_at
      content
      response_message
      response_sent_at
      response_media_url
      response_twilio_sid
      voter_id
      voter_state
      incoming_smses_organization {
        sms_provider
      }
    }
    ri_apps_prd_incoming_smses_aggregate(
      where: {
        _and: [
          { receiving_number: { _eq: $phone_number } }
          { sent_at: { _gte: $after } }
          { sent_at: { _lte: $before } }
          $search
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_VOTERS_FOR_TEXT_REPLIES = gql`
  query GetVotersForTextReplies(
    $filters: ri_apps_prd_voters_comb_bool_exp = {}
    $limit: Int
    $offset: Int
  ) {
    rows: ri_apps_prd_voters_comb(
      where: { _and: [$filters] }
      limit: $limit
      offset: $offset
      order_by: [{ last_name: asc }, { first_name: asc }]
    ) {
      rnc_regid
      voter_id
      first_name
      last_name
      mobile_number
      reg_address1
      reg_address2
      reg_city
      reg_zip5
      state_voter_id
      state
      county
      precinct
      reg_party
      modeled_party: calcparty
      state_leg_upper_district
      state_leg_lower_district
      voter_general_likeliness
    }
  }
`;

export const BULK_UPDATE_INCOMING_SMSES = gql`
  mutation bulkUpdateIncomingSMSes($updates: [ri_apps_prd_incoming_smses_updates!]!) {
    update_ri_apps_prd_incoming_smses_many(updates: $updates) {
      affected_rows
      returning {
        id
        response_message
        response_sent_at
        response_media_url
        response_twilio_sid
      }
    }
  }
`;

export const GET_ORGANIZATION_OFFICE_TYPES = gql`
  query GetOrganizationOfficeTypes($org_id: Int!) {
    row: ri_apps_prd_organizations_by_pk(organization_id: $org_id) {
      organizations_office_types {
        org_office_types_office_type {
          office_type_id
          name
        }
        org_office_types_office_type_jurisdiction {
          jurisdiction_id
          name
        }
      }
    }
  }
`;

export const GET_BAD_NUMBERS_BY_ORG_STATE_AND_DATE = gql`
  query searchBadNumbers($where: ri_apps_prd_bad_numbers_bool_exp!, $limit: Int, $offset: Int) {
    ri_apps_prd_bad_numbers(where: $where, limit: $limit, offset: $offset) {
      bad_number_id
      org_id
      voter_id
      state
      voter_mobile
      error_code
      bad_number_voter {
        first_name
        last_name
        reg_address1
        reg_address1
        reg_address2
        reg_address2
        reg_city
        reg_zip5
        state
        county
      }
      bad_number_organization {
        name
      }
    }
    ri_apps_prd_bad_numbers_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_VOTER_INCOMING_SMSES = gql`
  query GetVoterReplies($where: ri_apps_prd_incoming_smses_bool_exp!) {
    rows: ri_apps_prd_incoming_smses(where: $where) {
      id
      voter_id
      organization_id
      content
      response_message
      sent_at
      response_sent_at
    }
  }
`;

export const GET_ORG_CHECKLIST_ACTIONS = gql`
  query GetOrgChecklistActions {
    rows: ri_apps_prd_org_check_list_actions(order_by: { order: asc }) {
      action_id
      name
      order
    }
  }
`

export const GET_ORG_CHECKLIST_STATUSES = gql`
  query GetOrgChecklistStatuses($where: ri_apps_prd_org_check_list_status_bool_exp!) {
    rows: ri_apps_prd_org_check_list_status(where: $where) {
      organization_id
      action_id
      status
    }
  }
`

export const UPSERT_ORG_CHECKLIST_STATUSES = gql`
  mutation UpsertOrgChecklistStatuses($data: [ri_apps_prd_org_check_list_status_insert_input!]!) {
    insert_ri_apps_prd_org_check_list_status(objects: $data, 
      on_conflict: { constraint: org_check_list_status_pkey, update_columns: [status] }
    ) {
      affected_rows
    }
  }
`